import { FC, useCallback, useMemo, useState } from "react";
import { ImageType, Product } from "@utilities/types";
import { formatPrice, getProductSellingPrice, getProductPurchasePrice } from "@utilities/functions";
import { Modal } from "@components";
import { ReadProductVariation } from "./ReadProductVariation";

interface ProductDetailsCardProps {
    product: Product | null;
    isOpen: boolean;
    onClose: () => void;
}

export const ProductDetailsCard: FC<ProductDetailsCardProps> = ({ product, isOpen, onClose }) => {

    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const images = useMemo(() => product?.images || [], [product]);

    const price = useMemo(()=> (
        product ? getProductSellingPrice(product) : 0
    ), [product]);

    const purchasePrice = useMemo(()=> (
        product ? getProductPurchasePrice(product) : 0
    ), [product]);

    const handlePrev = useCallback(() => {
        setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    }, [images]);

    const handleNext = useCallback(() => {
        setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, [images]);

    const displayImage = useCallback((img: ImageType) => {
        return `${process.env.REACT_APP_IMAGE_BASE_URL}/${img.fileDir}/${img.filename}`;
    }, []);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="🛍️ Détails du produit"
        >
            <div className="space-y-6 text-gray-800">
                {/* Header */}
                <div className="flex justify-between items-center border-b pb-4">
                    <div>
                        <h2 className="text-2xl font-extrabold">{product?.name}</h2>
                            <span
                            className={`inline-block mt-1 px-3 py-0.5 text-xs font-semibold rounded-full ${
                                product?.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            }`}
                        >
                            {product?.isActive ? "Actif" : "Inactif"}
                        </span>
                    </div>
                </div>

                {/* Images Carousel */}
                {images.length > 0 && (
                <div className="relative w-full h-[300px] overflow-hidden rounded-xl border bg-gray-50">
                    <img
                        src={displayImage(images[currentImageIndex])}
                        alt={`Image ${currentImageIndex + 1}`}
                        className="w-full h-full object-contain"
                    />
                    {images.length > 1 && (
                        <>
                            <button
                                className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white hover:bg-gray-100 border border-gray-300 text-sm px-2 py-1 rounded-full shadow"
                                onClick={handlePrev}
                            >
                                ◀
                            </button>
                            <button
                                className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white hover:bg-gray-100 border border-gray-300 text-sm px-2 py-1 rounded-full shadow"
                                onClick={handleNext}
                            >
                                ▶
                            </button>
                        </>
                    )}
                </div>
                )}

                {/* Description + Price */}
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-500">💰 Prix de vente</h3>
                            <p className="text-xl font-bold text-primary">{formatPrice(price)}</p>
                        </div>
                        {purchasePrice > 0 && (
                            <div>
                                <h3 className="text-sm font-semibold text-gray-500">🛒 Prix d'achat</h3>
                                <p className="text-lg font-semibold text-secondary">{formatPrice(purchasePrice)}</p>
                            </div>
                        )}
                    </div>

                    <div>
                        <h3 className="text-sm font-semibold text-gray-500">📝 Description</h3>
                        <p className="text-sm text-gray-700">{product?.description || "Aucune description disponible."}</p>
                    </div>
                </div>

                {/* Variations */}
                <div>
                    <h3 className="text-sm font-semibold text-gray-500 mb-1">🧩 Variations</h3>
                    <ReadProductVariation variation={product?.variations} />
                </div>
            </div>
        </Modal>
    );
};
